import { useState, useEffect, useCallback } from 'react';

// LocalStorage key - if this exists, user has seen the popup
const STORAGE_KEY_SEEN = 'ask-aldo-feedback-prompt-seen';

interface UseFeedbackRequestReturn {
  /** Whether the feedback request modal should be shown */
  shouldShowFeedbackRequest: boolean;
  /** Call this when user dismisses or acknowledges the popup */
  dismissFeedbackRequest: () => void;
}

/**
 * Hook to manage one-time feedback request popup
 * 
 * Shows a popup on first app load requesting users to use thumbs up/down feedback.
 * Once shown (dismissed or acknowledged), never shows again for that browser.
 * 
 * Persisted in localStorage - clears on cache clear or new browser/device.
 */
export const useFeedbackRequest = (): UseFeedbackRequestReturn => {
  const [shouldShow, setShouldShow] = useState(false);

  // Check localStorage on mount to see if user has seen the popup before
  useEffect(() => {
    try {
      const hasSeenBefore = localStorage.getItem(STORAGE_KEY_SEEN) === 'true';
      // Show popup if user has NOT seen it before
      setShouldShow(!hasSeenBefore);
    } catch (error) {
      console.warn('Failed to check feedback request state:', error);
      // Don't show on error to avoid annoying users
      setShouldShow(false);
    }
  }, []);

  // Dismiss the popup - marks as seen so it never shows again
  const dismissFeedbackRequest = useCallback(() => {
    setShouldShow(false);
    try {
      localStorage.setItem(STORAGE_KEY_SEEN, 'true');
    } catch (error) {
      console.warn('Failed to save feedback prompt state:', error);
    }
  }, []);

  return {
    shouldShowFeedbackRequest: shouldShow,
    dismissFeedbackRequest
  };
};

export default useFeedbackRequest;

